<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const props = withDefaults(
  defineProps<{
    size?: number | string;
    width?: number | string;
    color?: string;
    label?: string | null;
    vertical?: boolean;
    progress?: number | null;
    showPercent?: boolean;
  }>(),
  {
    size: 45,
    width: 5,
    color: "primary",
    vertical: true,
    progress: null,
    showPercent: true,
  }
);

const { t } = useI18n();

const normalizedSize = computed(() =>
  typeof props.size === "number" ? props.size : Number.parseInt(props.size, 10) || 40
);

const normalizedWidth = computed(() =>
  typeof props.width === "number" ? props.width : Number.parseInt(props.width, 10) || 4
);

const normalizedProgress = computed<number | null>(() => {
  if (typeof props.progress !== "number" || !Number.isFinite(props.progress)) {
    return null;
  }

  return Math.min(100, Math.max(0, Math.round(props.progress)));
});

const isIndeterminate = computed(() => normalizedProgress.value === null);

const labelText = computed(() => {
  if (props.label === null) return "";

  return props.label ?? t("generic.states.loading");
});

const wrapperClass = computed(() => [
  "tw:inline-flex tw:items-center",
  props.vertical ? "tw:flex-col tw:gap-3" : "tw:flex-row tw:gap-2",
]);
</script>

<template>
  <div
    :class="wrapperClass"
    role="status"
    aria-live="polite"
    :aria-label="labelText || t('generic.states.loading')"
  >
    <v-progress-circular
      :size="normalizedSize"
      :width="normalizedWidth"
      :color="color"
      :indeterminate="isIndeterminate"
      :model-value="progress ?? 0"
    >
      <span
        v-if="!isIndeterminate && showPercent"
        class="tw:text-xs! tw:font-semibold"
      >
        {{ normalizedProgress }}%
      </span>
    </v-progress-circular>

    <span
      v-if="labelText"
      class="loading-label tw:text-sm tw:font-semibold tw:text-on-surface"
    >
      {{ labelText }}
    </span>

    <slot />
  </div>
</template>
